// domainOverlap — pure helpers for computing REAL project-domain overlap
// between an ecosystem profile (or directory entity) and the caller's own
// projects. No fabricated scores: a project only counts as "relevant" when
// its `vertical` field genuinely overlaps one of the given domain strings
// (case-insensitive, substring-tolerant to absorb free-text variance like
// "Biotech" vs "Biotechnology" — `vertical` is a free-text field with
// datalist suggestions, not a fixed enum, so exact string equality would
// under-match real overlaps).
import type { Project } from '../../types'

function normalize(s: string): string {
  return s.trim().toLowerCase()
}

// Short/common words that would otherwise cause false-positive overlaps via
// the token-overlap fallback below (e.g. "Materials Science" vs "Data
// Science" sharing only the word "science").
const STOPWORDS = new Set(['and', 'the', 'for', 'science', 'technology', 'tech', 'services'])

/** True when two free-text domain/vertical strings genuinely refer to the same field. */
export function domainsOverlap(a: string, b: string): boolean {
  const na = normalize(a)
  const nb = normalize(b)
  if (!na || !nb) return false
  if (na === nb) return true
  if (na.includes(nb) || nb.includes(na)) return true
  const wordsA = na.split(/[^a-z0-9]+/).filter((w) => w.length >= 4 && !STOPWORDS.has(w))
  const wordsB = new Set(nb.split(/[^a-z0-9]+/).filter((w) => w.length >= 4 && !STOPWORDS.has(w)))
  return wordsA.some((w) => wordsB.has(w))
}

export interface ProjectRelevance {
  /** number of the caller's active projects with a real, computed domain overlap */
  count: number
  /** the actual overlapping domain labels (deduped, original casing from the input domains) */
  sharedDomains: string[]
}

/**
 * Computes REAL relevance between a domain list (an ecosystem profile's
 * `domains`, or a directory entity's `topics`) and the caller's projects.
 * Only projects with a non-empty `vertical` are considered — a project with
 * no vertical set can never be scored as "relevant" (no fake defaulting).
 *
 * Returns { count: 0, sharedDomains: [] } whenever a real overlap cannot be
 * computed (no domains, no projects, or no project has a vertical) — the
 * caller should render nothing rather than a fabricated relevance number.
 */
export function computeProjectRelevance(
  domains: string[] | undefined,
  projects: Project[]
): ProjectRelevance {
  const list = (domains ?? []).filter(Boolean)
  if (list.length === 0 || projects.length === 0) return { count: 0, sharedDomains: [] }

  let count = 0
  const shared = new Set<string>()
  for (const project of projects) {
    const vertical = project.vertical?.trim()
    if (!vertical) continue
    const matched = list.filter((d) => domainsOverlap(d, vertical))
    if (matched.length > 0) {
      count += 1
      matched.forEach((m) => shared.add(m))
    }
  }
  return { count, sharedDomains: [...shared] }
}
